import React from 'react';
import { DecisionStatus } from '../types/decision';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  HelpCircle,
  ShieldAlert,
  Clock,
  TrendingUp,
  Percent
} from 'lucide-react';

interface MetricSummaryProps {
  total: number;
  applicableCount: number;
  notApplicableCount: number;
  unresolvedCount: number;
  unassessedCount: number;
  activeStatusFilter: string;
  onSelectStatusFilter: (status: string) => void;
}

export default function MetricSummary({
  total,
  applicableCount,
  notApplicableCount,
  unresolvedCount,
  unassessedCount,
  activeStatusFilter,
  onSelectStatusFilter,
}: MetricSummaryProps) {
  const assessedCount = total - unassessedCount;
  const progressPercent = total > 0 ? Math.round((assessedCount / total) * 100) : 0;
  const applicablePercent = total > 0 ? Math.round((applicableCount / total) * 100) : 0;
  const unresolvedPercent = total > 0 ? Math.round((unresolvedCount / total) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* 4 Interactive KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        
        {/* Applicable Card */}
        <button
          onClick={() => onSelectStatusFilter(activeStatusFilter === 'APPLICABLE' ? 'ALL' : 'APPLICABLE')}
          className={`p-4 rounded-2xl border text-left transition-all ${
            activeStatusFilter === 'APPLICABLE'
              ? 'bg-emerald-500/15 border-emerald-500 shadow-md ring-2 ring-emerald-500/30'
              : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-emerald-400 dark:hover:border-emerald-600 shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
            <span>Applicable</span>
            <CheckCircle2 className="w-4 h-4" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-slate-100">{applicableCount}</span>
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">({applicablePercent}%)</span>
          </div>
          <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            Active in-scope duties
          </div>
        </button>

        {/* Not Applicable Card */}
        <button
          onClick={() => onSelectStatusFilter(activeStatusFilter === 'NOT_APPLICABLE' ? 'ALL' : 'NOT_APPLICABLE')}
          className={`p-4 rounded-2xl border text-left transition-all ${
            activeStatusFilter === 'NOT_APPLICABLE'
              ? 'bg-slate-200/80 dark:bg-slate-800 border-slate-400 dark:border-slate-600 shadow-md ring-2 ring-slate-400/30'
              : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-600 shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
            <span>Not Applicable</span>
            <XCircle className="w-4 h-4" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-slate-100">{notApplicableCount}</span>
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
              ({total > 0 ? Math.round((notApplicableCount / total) * 100) : 0}%)
            </span>
          </div>
          <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            Exempted or out of scope
          </div>
        </button>

        {/* Unresolved Blockers Card */}
        <button
          onClick={() => onSelectStatusFilter(activeStatusFilter === 'UNRESOLVED' ? 'ALL' : 'UNRESOLVED')}
          className={`p-4 rounded-2xl border text-left transition-all ${
            activeStatusFilter === 'UNRESOLVED'
              ? 'bg-amber-500/15 border-amber-500 shadow-md ring-2 ring-amber-500/30'
              : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-amber-400 dark:hover:border-amber-600 shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
            <span>Unresolved</span>
            <AlertCircle className="w-4 h-4" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-black text-amber-600 dark:text-amber-400">{unresolvedCount}</span>
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">({unresolvedPercent}%)</span>
          </div>
          <div className="mt-1 text-[11px] text-amber-700/80 dark:text-amber-400/80 font-medium">
            Requires legal/fact review
          </div>
        </button>

        {/* Unassessed / Progress Card */}
        <button
          onClick={() => onSelectStatusFilter(activeStatusFilter === 'UNASSESSED' ? 'ALL' : 'UNASSESSED')}
          className={`p-4 rounded-2xl border text-left transition-all ${
            activeStatusFilter === 'UNASSESSED'
              ? 'bg-accent/15 border-accent shadow-md ring-2 ring-accent/30'
              : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-accent shadow-xs'
          }`}
        >
          <div className="flex items-center justify-between text-xs font-semibold text-accent dark:text-accent-light uppercase tracking-wider">
            <span>Assessment Progress</span>
            <TrendingUp className="w-4 h-4" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-slate-100">{progressPercent}%</span>
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">({assessedCount}/{total})</span>
          </div>
          <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            {unassessedCount} items pending
          </div>
        </button>

      </div>

      {/* Segmented Progress Bar */}
      <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
            <Percent className="w-3.5 h-3.5 text-accent" />
            <span>Compliance Scope Composition</span>
          </span>
          <span className="text-slate-500 dark:text-slate-400 font-mono text-[11px]">
            {assessedCount} of {total} evaluated
          </span>
        </div>

        <div className="w-full h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden flex">
          {applicableCount > 0 && (
            <div
              style={{ width: `${(applicableCount / total) * 100}%` }}
              className="bg-emerald-500 transition-all duration-500 hover:opacity-90"
              title={`Applicable: ${applicableCount}`}
            />
          )}
          {notApplicableCount > 0 && (
            <div
              style={{ width: `${(notApplicableCount / total) * 100}%` }}
              className="bg-slate-400 dark:bg-slate-600 transition-all duration-500 hover:opacity-90"
              title={`Not Applicable: ${notApplicableCount}`}
            />
          )}
          {unresolvedCount > 0 && (
            <div
              style={{ width: `${(unresolvedCount / total) * 100}%` }}
              className="bg-amber-500 transition-all duration-500 hover:opacity-90"
              title={`Unresolved: ${unresolvedCount}`}
            />
          )}
          {unassessedCount > 0 && (
            <div
              style={{ width: `${(unassessedCount / total) * 100}%` }}
              className="bg-slate-200 dark:bg-slate-700/40 transition-all duration-500"
              title={`Unassessed: ${unassessedCount}`}
            />
          )}
        </div>

        <div className="flex flex-wrap items-center gap-4 text-[11px] text-slate-500 dark:text-slate-400 pt-1">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
            <span>Applicable ({applicableCount})</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-slate-400 dark:bg-slate-600 inline-block" />
            <span>Not Applicable ({notApplicableCount})</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" />
            <span>Unresolved ({unresolvedCount})</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-slate-200 dark:bg-slate-700 inline-block" />
            <span>Pending ({unassessedCount})</span>
          </span>
        </div>
      </div>
    </div>
  );
}
